import React, {useMemo, useState} from "react";
import {
    Building,
    Calendar,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Copy,
    Eye,
    FileText,
    Key,
    Package,
    Pencil,
    Plus,
    Shield,
    Trash2,
    XCircle
} from "lucide-react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {Search as SearchIcon} from "@mui/icons-material";
import {FeedbackSnackbar} from "../../shared/ui";
import {usePermissions} from "./usePermissions";
import {countPermissions} from "./permissionsUtils";
import PermissionDialog from "./components/PermissionDialog";

export default function PermissionsPage() {
    const [currentPage, setCurrentPage] = useState(1);
    const data = usePermissions();
    const itemsPerPage = 10;

    const rows = useMemo(() => data.rows.filter((row) => {
        const query = data.filter.trim().toLowerCase();
        const matchesQuery = !query
            || (row.roleName || row.nome || '').toLowerCase().includes(query)
            || (row.description || row.descricao || '').toLowerCase().includes(query);
        const matchesLicense = !data.selectedLicencaId || row.id_licenca === data.selectedLicencaId;
        return matchesQuery && matchesLicense;
    }), [data.filter, data.rows, data.selectedLicencaId]);

    const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
    const currentPageSafe = Math.min(currentPage, totalPages);
    const startIndex = (currentPageSafe - 1) * itemsPerPage;
    const paginatedRows = rows.slice(startIndex, startIndex + itemsPerPage);
    const licenseStatusColor = data.selectedLicenca?.status === 'ATIVA' ? 'success' : data.selectedLicenca?.status === 'EM_ATRASO' ? 'error' : 'warning';

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h4" fontWeight={700}>Políticas de Acesso</Typography>
                <Typography color="text.secondary">
                    Gerencie roles e permissões a partir do contexto da licença selecionada.
                </Typography>
            </Box>

            <FeedbackSnackbar
                feedback={data.feedback}
                onClose={() => data.setFeedback({error: '', success: ''})}
            />

            <Box sx={{bgcolor: 'background.paper', border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 3, p: 3}}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
                    <Shield size={20} color="#0f766e" />
                    <Typography variant="h6" fontWeight={700}>Licença Selecionada</Typography>
                </Stack>
                <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5} sx={{mb: 2}}>
                    <TextField
                        select
                        label="Licença"
                        value={data.selectedLicencaId}
                        onChange={(event) => {
                            data.setSelectedLicencaId(event.target.value);
                            setCurrentPage(1);
                        }}
                        sx={{minWidth: {md: 340}}}
                    >
                        {data.references.licencas.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {(item.empresa?.razao_social || item.empresa?.nome || 'Empresa sem nome')} - {item.chave_ativacao || item.id}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)'}, gap: 2}}>
                    <Box sx={{bgcolor: 'grey.50', borderRadius: 2, p: 2}}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                            <Building size={16} color="#6b7280" />
                            <Typography variant="caption" color="text.secondary">Empresa</Typography>
                        </Stack>
                        <Typography>{data.selectedLicenca?.empresa?.razao_social || data.selectedLicenca?.empresa?.nome || '-'}</Typography>
                    </Box>
                    <Box sx={{bgcolor: 'grey.50', borderRadius: 2, p: 2}}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                            <FileText size={16} color="#6b7280" />
                            <Typography variant="caption" color="text.secondary">Licença</Typography>
                        </Stack>
                        <Typography variant="body2">{(data.selectedLicenca?.id || '-').slice(0, 13)}{data.selectedLicenca?.id ? '...' : ''}</Typography>
                    </Box>
                    <Box sx={{bgcolor: 'grey.50', borderRadius: 2, p: 2}}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                            <Package size={16} color="#6b7280" />
                            <Typography variant="caption" color="text.secondary">Plano</Typography>
                        </Stack>
                        <Typography>{data.selectedLicenca?.plano?.nome_plano || '-'}</Typography>
                    </Box>
                    <Box sx={{bgcolor: 'grey.50', borderRadius: 2, p: 2}}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                            <Key size={16} color="#6b7280" />
                            <Typography variant="caption" color="text.secondary">Tenant ID</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{fontFamily: 'monospace'}}>{data.selectedLicenca?.tenant_id || '-'}</Typography>
                    </Box>
                    <Box sx={{bgcolor: 'grey.50', borderRadius: 2, p: 2}}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                            <CheckCircle size={16} color="#6b7280" />
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                        </Stack>
                        <Chip size="small" color={licenseStatusColor} label={data.selectedLicenca?.status || '-'} />
                    </Box>
                    <Box sx={{bgcolor: 'grey.50', borderRadius: 2, p: 2}}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                            <Calendar size={16} color="#6b7280" />
                            <Typography variant="caption" color="text.secondary">Validade</Typography>
                        </Stack>
                        <Typography variant="body2">{data.selectedLicenca?.validade_inicio_fmt || '-'} até {data.selectedLicenca?.validade_fim_fmt || '-'}</Typography>
                    </Box>
                </Box>
            </Box>

            <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5}>
                <TextField
                    placeholder="Buscar role por nome ou descrição..."
                    value={data.filter}
                    onChange={(event) => {
                        data.setFilter(event.target.value);
                        setCurrentPage(1);
                    }}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{opacity: 0.65}} />
                            </InputAdornment>
                        )
                    }}
                />
                <Button variant="contained" startIcon={<Plus size={18} />} onClick={data.openCreate}>
                    Nova Role
                </Button>
            </Stack>

            <Box sx={{bgcolor: 'background.paper', border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, overflow: 'hidden'}}>
                <Box sx={{overflowX: 'auto'}}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{bgcolor: 'grey.50'}}>
                                <TableCell>Role</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Permissões Liberadas</TableCell>
                                <TableCell>Permissões Bloqueadas</TableCell>
                                <TableCell>Atualizado em</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{py: 6}}>
                                        <CircularProgress size={20} />
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {!paginatedRows.length && !data.loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{py: 6}}>
                                        Nenhuma role encontrada.
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {paginatedRows.map((row) => {
                                const resumoPermissoes = countPermissions(row.permissions);
                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Typography>{row.roleName || row.nome || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary" sx={{maxWidth: 320}} noWrap>
                                                {row.description || row.descricao || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CheckCircle size={16} color="#16a34a" />
                                                <Typography>{resumoPermissoes.liberadas}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <XCircle size={16} color="#dc2626" />
                                                <Typography>{resumoPermissoes.bloqueadas}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{row.atualizado_em_fmt || row.ultima_revisao_fmt || '-'}</TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => data.openView(row)}
                                                    sx={{color: '#0f766e'}}
                                                >
                                                    <Eye size={16} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => data.openEdit(row)} sx={{color: '#1d4ed8'}}>
                                                    <Pencil size={16} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => data.duplicateRole(row)} sx={{color: '#2563eb'}}>
                                                    <Copy size={16} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => data.setConfirmDelete(row)} sx={{color: '#dc2626'}}>
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
                <Box sx={{px: 3, py: 2, borderTop: (t) => `1px solid ${t.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                    <Typography variant="body2" color="text.secondary">
                        Mostrando {rows.length ? startIndex + 1 : 0} a {Math.min(startIndex + itemsPerPage, rows.length)} de {rows.length} roles
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setCurrentPage((value) => Math.max(1, value - 1))}
                            disabled={currentPageSafe === 1}
                            startIcon={<ChevronLeft size={16} />}
                        >
                            Anterior
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                            Página {currentPageSafe} de {totalPages}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setCurrentPage((value) => Math.min(totalPages, value + 1))}
                            disabled={currentPageSafe === totalPages}
                            endIcon={<ChevronRight size={16} />}
                        >
                            Próxima
                        </Button>
                    </Stack>
                </Box>
            </Box>

            <PermissionDialog
                open={data.dialogOpen}
                form={data.form}
                references={data.references}
                recursosDisponiveis={data.recursosDisponiveis}
                selectedLicenca={data.selectedLicenca}
                saving={data.saving}
                editing={data.editing}
                onClose={() => data.setDialogOpen(false)}
                onSubmit={data.submit}
            />

            <Dialog open={Boolean(data.confirmDelete)} onClose={() => data.setConfirmDelete(null)}>
                <DialogTitle>Excluir Role</DialogTitle>
                <DialogContent>
                    Tem certeza que deseja excluir a role <strong>{data.confirmDelete?.roleName || data.confirmDelete?.nome || '-'}</strong>? Esta ação não pode ser desfeita e removerá todas as permissões associadas.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => data.setConfirmDelete(null)}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={data.confirmDeleteTarget}>Excluir</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
